import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { KeycloakContext } from "@context/KeycloakContext";
import { useScope } from "@context/ScopeContext.jsx";

import "./SearchInput.css";
import SEARCH from "@assets/icons/Search.svg?react";

const SearchInput = ({ placeholder = "Search...", ...props }) => {
  const navigate = useNavigate();
  const { userType, userFeatures, userPermissions } =
    useContext(KeycloakContext);
  const { selectedScope } = useScope();

  const [value, setValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isDivHovered, setIsDivHovered] = useState(false);
  const [pagesAvailable, setPagesAvailable] = useState();
  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    const pages = [{ name: "Settings - Account", url: "/settings/account" }];

    if (userFeatures) {
      userFeatures.forEach((d) => {
        if (d.startsWith("dashboard-")) {
          const dashboardId = d.replace("dashboard-", "");
          let capitalized = "";
          let path = dashboardId;
          if (dashboardId === "vuln") {
            capitalized = "Vulnerabilities";
            path = "vulnerabilities";
          } else if (dashboardId === "darkweb") {
            capitalized = "Dark Web Monitoring";
          } else {
            capitalized = dashboardId.charAt(0).toUpperCase() + dashboardId.slice(1);
          }
          pages.push({ name: `Dashboard - ${capitalized}`, url: `/dashboards/${path}` });
        }
      });
    }

    if (userPermissions.has("read-organization")) {
      pages.push({ name: "Settings - Organization", url: "/settings/org" });
      pages.push({ name: "Settings - Report Schedules", url: "/settings/report-schedules" });
    }

    if (userPermissions.has("read-users"))
      pages.push({ name: "Settings - Users", url: "/settings/users" });

    if (userPermissions.has("read-roles"))
      pages.push({ name: "Settings - Roles", url: "/settings/roles" });

    if (userPermissions.has("read-tokens"))
      pages.push({ name: "Settings - API Tokens", url: "/settings/tokens" });

    if (userPermissions.has("read-investigations"))
      pages.push({ name: "Investigations", url: "/investigations" });

    if (userType === "employee") {
      if (userPermissions.has("read-partners"))
        pages.push({ name: "Settings - Partners", url: "/settings/partners" });

      if (selectedScope?.type === "NCO") {
        pages.push({ name: "Admin Tools - JSON Combiner", url: "/admin-tools/json" });
        pages.push({ name: "Admin Tools - Theme Colors", url: "/admin-tools/theme-colors" });
        pages.push({ name: "Admin Tools - API Docs", url: "/swagger" });

        if (userPermissions.has("update-customers"))
          pages.push({ name: "Admin Tools - Customers Products", url: "/admin-tools/customers-products" });

        if (userPermissions.has("create-notifications"))
          pages.push({ name: "Admin Tools - Email Testing", url: "/admin-tools/email" });

        if (userPermissions.has("update-investigations"))
          pages.push({ name: "Admin Tools - Investigation On Call Testing", url: "/admin-tools/paging" });

        if (userPermissions.has("update-partners") && userPermissions.has("update-customers"))
          pages.push({ name: "Admin Tools - Notification Settings", url: "/admin-tools/notification-settings" });

        if (userPermissions.has("update-partners") || userPermissions.has("update-customers"))
          pages.push({ name: "Admin Tools - Permission Settings", url: "/admin-tools/permissions" });
      }
    }

    if (userType !== "customer") {
      if (userPermissions.has("read-customers"))
        pages.push({ name: "Settings - Customers", url: "/settings/customers" });

      if (userPermissions.has("read-skus"))
        pages.push({ name: "SKUs", url: "/sku" });
    }

    if (pages.length > 0) setPagesAvailable(pages);
  }, [userFeatures, userType, userPermissions, selectedScope]);

  const handleInputChange = (e) => {
    setValue(e.target.value);
    const query = e.target.value.toLowerCase();

    if (query) {
      const results = pagesAvailable.filter((item) =>
        item.name.toLowerCase().includes(query)
      );
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  };

  return (
    <div className="search-container">
      <div className={`search-container-top-bar${isFocused ? " focused" : ""}`}>
        <SEARCH className="search-container-top-bar__search-icon" />
        <input
          className="search-container-top-bar__search-box"
          placeholder={placeholder}
          onChange={handleInputChange}
          value={value}
          {...props}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {(isDivHovered || isFocused) && filteredResults.length > 0 && (
          <div
            className="search-results"
            onMouseEnter={() => setIsDivHovered(true)}
            onMouseLeave={() => setIsDivHovered(false)}
          >
            {filteredResults.map((result, index) => (
              <div
                key={index}
                className="search-result-item"
                onClick={() => {
                  setIsDivHovered(false);
                  setFilteredResults([]);
                  setValue("");
                  navigate(result.url);
                }}
              >
                {result.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Define propTypes for the component to specify that props are optional
SearchInput.propTypes = {
  placeholder: PropTypes.string,
  // You can define types for other props as well
};

export default SearchInput;
